import { usePostGroupTrainingLedgerApi, usePutGroupTrainingLedgerApi } from 'api/group_training_ledger'
import { Flex } from 'components/shared/flex'
import { BooleanRadioField, DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { GroupTraining, GroupTrainingForm } from 'entities/group_training'
import 'rc-time-picker/assets/index.css'

const GroupTrainingFormView = (form: Form<GroupTraining>, apiError: ApiError, isNew?: boolean) => {
    const globalState = useContext(GlobalStateContext)
    return (
        <Flex flexDirection="column">
            <Flex flexDirection="column">
                <InputField form={form} apiError={apiError} required attr="sessionName" label="講習会名" />
                <Flex>
                    <DateField form={form} attr="date" label="実施日" />
                    <SelectField
                        form={form}
                        attr="trainerId"
                        label="トレーナー"
                        selectItems={globalState.users.map((u) => ({ label: u.fullName!, value: u.id! }))}
                    />
                </Flex>
                <InputField form={form} attr="url" label="教材URL" />
            </Flex>
        </Flex>
    )
}

const AttendInfoFormView = (form: Form<GroupTraining>, apiError: ApiError, isNew?: boolean) => {
    const globalState = useContext(GlobalStateContext)
    return (
        <Flex flexDirection="column">
            <Flex flexDirection="column">
                <Flex>
                    <InputField form={form} attr="trainer" label="受講者" placeholder={globalState.user.fullName} disabled />
                    <DateField form={form} attr={['groupTrainingTrainees', 0, 'date']} label="受講日" />
                </Flex>
                <BooleanRadioField
                    positiveLabel="よく理解できた"
                    negativeLabel="理解できなかったので再履修希望"
                    form={form}
                    attr={['groupTrainingTrainees', 0, 'isOk']}
                />
            </Flex>
        </Flex>
    )
}

type NewGroupTrainingFormProps = {
    onComplete?: () => void
    disabled?: boolean
}

export const NewGroupTrainingForm = (props: NewGroupTrainingFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostGroupTrainingLedgerApi()
    const form = useForm<GroupTrainingForm>({})
    const [show, setShow] = useState<boolean>(false)
    console.log(form.object)

    const handleUpdate = () => {
        api.execute(form)
    }

    const handleClose = () => {
        setShow(false)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <>
            <Button
                disabled={props.disabled}
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal onShow={() => form.updateObject('trainerId', globalState.user.id)} fullscreen show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>講習会情報の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{GroupTrainingFormView(form, api.apiError, true)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditGroupTrainingFormProps = {
    groupTraining: GroupTraining
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditGroupTrainingForm = (props: EditGroupTrainingFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutGroupTrainingLedgerApi()
    const form = useForm<GroupTrainingForm>(props.groupTraining)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        const groupTrainingTrainee = props.groupTraining.groupTrainingTrainees?.filter((t) => t.trainee?.id === globalState.user.id) ?? []
        if (groupTrainingTrainee?.length === 0) {
            groupTrainingTrainee.push({ traineeId: globalState.user.id, groupTrainingLedgerId: form.object.id })
        }
        props.groupTraining.groupTrainingTrainees = groupTrainingTrainee
        form.set(props.groupTraining)
    }, [props.groupTraining])

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal
            show={props.show}
            onHide={props.onClose}
            onShow={() => {
                const groupTrainingTrainee = form.object.groupTrainingTrainees?.filter((t) => t.trainee?.id === globalState.user.id) ?? []
                if (groupTrainingTrainee?.length === 0) {
                    groupTrainingTrainee.push({ traineeId: globalState.user.id, groupTrainingLedgerId: form.object.id })
                }
                form.updateObject('groupTrainingTrainees', groupTrainingTrainee)
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>集合教育記録台帳の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <>{GroupTrainingFormView(form, api.apiError)}</>}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

type EditAttendInfoFormProps = {
    groupTraining: GroupTraining
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditAttendInfoForm = (props: EditAttendInfoFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutGroupTrainingLedgerApi()
    const form = useForm<GroupTrainingForm>(props.groupTraining)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        const groupTrainingTrainee = props.groupTraining.groupTrainingTrainees?.filter((t) => t.trainee?.id === globalState.user.id) ?? []
        if (groupTrainingTrainee?.length === 0) {
            groupTrainingTrainee.push({ traineeId: globalState.user.id, groupTrainingLedgerId: form.object.id })
        }
        props.groupTraining.groupTrainingTrainees = groupTrainingTrainee
        form.set(props.groupTraining)
    }, [props.groupTraining])

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal
            show={props.show}
            onHide={props.onClose}
            onShow={() => {
                const groupTrainingTrainee = form.object.groupTrainingTrainees?.filter((t) => t.trainee?.id === globalState.user.id) ?? []
                if (groupTrainingTrainee?.length === 0) {
                    groupTrainingTrainee.push({ traineeId: globalState.user.id, groupTrainingLedgerId: form.object.id })
                }
                form.updateObject('groupTrainingTrainees', groupTrainingTrainee)
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>受講情報の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <>{AttendInfoFormView(form, api.apiError)}</>}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
